/**
 * Reseller pool management — /admin/senders. Lives separately from the
 * customer-facing /senders page (which after migration 000024 is the
 * read-only "what's been assigned to me" view).
 *
 * Approval lifecycle the page surfaces:
 *   draft → pending_review → approved → assigned → unassigned → re-assigned
 *           ↘ rejected (with reason) → re-edit → re-submit
 *           ↘ approved + suspended (platform admin only — read-only here)
 */

import { type FormEvent, type JSX, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Button, Card, Input, Skeleton } from '@/components/ui';
import { useCustomers } from '@/hooks/useAdminCustomers';
import {
  useAdminSendersList,
  useAssignPoolSender,
  useCreatePoolSender,
  useDeletePoolSender,
  useSubmitPoolSender,
  useUnassignPoolSender,
  useWithdrawPoolSender,
  type PoolSender,
} from '@/hooks/useAdminSenders';
import type { SenderType } from '@/hooks/useSenders';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

const SENDER_TYPES: SenderType[] = ['long_code', 'toll_free', 'short_code', 'alphanumeric'];

type ExtendedStatus = PoolSender['registration_status'];

interface FormState {
  type: SenderType;
  value: string;
  country_code: string;
  use_case: string;
}

const INITIAL_FORM: FormState = {
  type: 'long_code',
  value: '',
  country_code: 'US',
  use_case: '',
};

function statusBadge(status: ExtendedStatus): { variant: 'success' | 'danger' | 'warn' | 'info' | 'gray'; label: string } {
  switch (status) {
    case 'approved':
      return { variant: 'success', label: 'approved' };
    case 'rejected':
      return { variant: 'danger', label: 'rejected' };
    case 'suspended':
      return { variant: 'danger', label: 'suspended' };
    case 'pending_review':
      return { variant: 'warn', label: 'pending review' };
    case 'pending':
    case 'submitted':
      return { variant: 'warn', label: status };
    case 'draft':
      return { variant: 'gray', label: 'draft' };
    case 'not_required':
      return { variant: 'info', label: 'no approval needed' };
    default:
      return { variant: 'gray', label: status };
  }
}

interface AssignBoxProps {
  senderID: number;
  onCancel: () => void;
  onAssigned: () => void;
}

function AssignBox({ senderID, onCancel, onAssigned }: AssignBoxProps): JSX.Element {
  const customers = useCustomers();
  const assign = useAssignPoolSender();
  const [selectedID, setSelectedID] = useState<number | ''>('');
  const [error, setError] = useState<string | null>(null);

  // Only assignable to active customers — suspended/closed shouldn't
  // get fresh assignments and would just fail at the API layer anyway.
  const candidates = (customers.data?.items ?? []).filter((c) => c.status === 'active');

  const submit = async () => {
    if (!selectedID) return;
    setError(null);
    try {
      await assign.mutateAsync({ id: senderID, customer_tenant_id: Number(selectedID) });
      onAssigned();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message ?? err.code);
      } else if (err instanceof Error) {
        setError(err.message);
      }
    }
  };

  return (
    <div className="flex items-center gap-2">
      <select
        value={selectedID}
        onChange={(e) => setSelectedID(e.target.value === '' ? '' : Number(e.target.value))}
        disabled={assign.isPending}
        className="h-8 px-2 text-xs bg-surface text-text border border-line-strong rounded-md outline-none focus:border-volt-500 disabled:opacity-50"
      >
        <option value="">Pick customer…</option>
        {candidates.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
      <Button size="sm" onClick={() => void submit()} disabled={!selectedID || assign.isPending}>
        Assign
      </Button>
      <Button size="sm" variant="secondary" onClick={onCancel} disabled={assign.isPending}>
        Cancel
      </Button>
      {error && <span className="text-[12px] text-danger ml-1">{error}</span>}
    </div>
  );
}

interface RowProps {
  s: PoolSender;
  busy: boolean;
  setError: (msg: string | null) => void;
}

function PoolRow({ s, busy, setError }: RowProps): JSX.Element {
  const submit = useSubmitPoolSender();
  const withdraw = useWithdrawPoolSender();
  const unassign = useUnassignPoolSender();
  const del = useDeletePoolSender();
  const [assignOpen, setAssignOpen] = useState(false);

  const status = s.registration_status;
  const badge = statusBadge(status);

  const wrap = async (fn: () => Promise<unknown>) => {
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  };

  return (
    <tr className="border-b border-line last:border-0 hover:bg-surface-2 align-top">
      <td className="px-5 py-3 num text-text whitespace-nowrap">{s.value}</td>
      <td className="px-5 py-3 text-text-2">{s.type}</td>
      <td className="px-5 py-3 num text-text-2 uppercase">{s.country_code}</td>
      <td className="px-5 py-3">
        <Badge variant={badge.variant}>{badge.label}</Badge>
        {status === 'approved' && s.auto_approved && (
          <div className="text-[11px] text-text-4 mt-1">auto-approved</div>
        )}
        {status === 'rejected' && s.rejection_reason && (
          <div className="text-[11px] text-danger mt-1 max-w-[200px]">{s.rejection_reason}</div>
        )}
        {status === 'suspended' && s.suspended_reason && (
          <div className="text-[11px] text-danger mt-1 max-w-[200px]">{s.suspended_reason}</div>
        )}
      </td>
      <td className="px-5 py-3 text-text-2">
        {s.assigned_to_tenant_id ? (
          <span>{s.assigned_to_name ?? `tenant ${s.assigned_to_tenant_id}`}</span>
        ) : (
          <span className="text-text-4">—</span>
        )}
      </td>
      <td className="px-5 py-3">
        <div className="flex items-center justify-end gap-2 flex-wrap">
          {assignOpen ? (
            <AssignBox senderID={s.id} onCancel={() => setAssignOpen(false)} onAssigned={() => setAssignOpen(false)} />
          ) : (
            <>
              {status === 'draft' && (
                <Button size="sm" onClick={() => void wrap(() => submit.mutateAsync(s.id))} disabled={busy}>
                  Submit
                </Button>
              )}
              {status === 'rejected' && (
                <Button size="sm" onClick={() => void wrap(() => submit.mutateAsync(s.id))} disabled={busy}>
                  Re-submit
                </Button>
              )}
              {status === 'pending_review' && (
                <Button size="sm" variant="secondary" onClick={() => void wrap(() => withdraw.mutateAsync(s.id))} disabled={busy}>
                  Withdraw
                </Button>
              )}
              {(status === 'approved' || status === 'not_required') && !s.assigned_to_tenant_id && (
                <Button size="sm" onClick={() => setAssignOpen(true)} disabled={busy}>
                  Assign
                </Button>
              )}
              {s.assigned_to_tenant_id && (
                <Button size="sm" variant="secondary" onClick={() => void wrap(() => unassign.mutateAsync(s.id))} disabled={busy}>
                  Unassign
                </Button>
              )}
              {(status === 'draft' || status === 'rejected') && !s.assigned_to_tenant_id && (
                <Button
                  size="sm"
                  className="bg-danger text-white hover:bg-danger/90"
                  onClick={() => void wrap(() => del.mutateAsync(s.id))}
                  disabled={busy}
                >
                  Delete
                </Button>
              )}
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

export default function AdminSendersPage(): JSX.Element {
  const { t } = useTranslation('default');
  const list = useAdminSendersList();
  const create = useCreatePoolSender();

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [createError, setCreateError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const items = list.data?.items ?? [];

  const counts = useMemo(() => {
    let approved = 0;
    let pending = 0;
    let assigned = 0;
    for (const s of items) {
      if (s.registration_status === 'approved' || s.registration_status === 'not_required') approved += 1;
      if (s.registration_status === 'pending_review') pending += 1;
      if (s.assigned_to_tenant_id) assigned += 1;
    }
    return { total: items.length, approved, pending, assigned };
  }, [items]);

  const handleCreate = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCreateError(null);
    if (!form.value.trim() || !/^[A-Z]{2}$/.test(form.country_code.trim().toUpperCase())) {
      setCreateError('Value and 2-letter country are required');
      return;
    }
    try {
      await create.mutateAsync({
        type: form.type,
        value: form.value.trim(),
        country_code: form.country_code.trim().toUpperCase(),
        use_case: form.use_case.trim() || undefined,
      });
      setForm(INITIAL_FORM);
    } catch (err) {
      if (err instanceof ApiError) {
        setCreateError(err.code === 'duplicate' ? 'A sender with this value + country already exists' : err.message ?? err.code);
      } else if (err instanceof Error) {
        setCreateError(err.message);
      }
    }
  };

  const busy = create.isPending;

  return (
    <div className="px-8 py-8 max-w-[1200px] mx-auto">
      <header className="mb-6">
        <h1 className="display text-[28px] font-semibold tracking-tight text-text leading-tight m-0">
          {t('nav_senders_pool', 'Sender pool')}
        </h1>
        <p className="text-[13px] text-text-3 mt-1">
          {counts.total === 0
            ? 'Add numbers to your pool, submit them for platform approval, then assign approved entries to your customers.'
            : `${counts.total} in pool · ${counts.approved} approved · ${counts.pending} pending · ${counts.assigned} assigned`}
        </p>
      </header>

      <Card padding="lg" className="mb-6">
        <h2 className="display text-[16px] font-semibold text-text mb-4">Add to pool</h2>
        {createError && (
          <div role="alert" className="px-3.5 py-3 bg-danger-bg border border-danger-bd rounded-md mb-4 text-[13px] text-danger">
            {createError}
          </div>
        )}
        <form onSubmit={handleCreate} noValidate>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <label htmlFor="ap-value" className="block text-[12px] font-medium text-text-2 mb-1.5">
                Value
              </label>
              <Input
                id="ap-value"
                placeholder="+15551234567"
                value={form.value}
                onChange={(e) => setForm((p) => ({ ...p, value: e.target.value }))}
                disabled={busy}
              />
            </div>
            <div>
              <label htmlFor="ap-country" className="block text-[12px] font-medium text-text-2 mb-1.5">
                Country
              </label>
              <Input
                id="ap-country"
                placeholder="US"
                maxLength={2}
                className="uppercase"
                value={form.country_code}
                onChange={(e) => setForm((p) => ({ ...p, country_code: e.target.value.toUpperCase().slice(0, 2) }))}
                disabled={busy}
              />
            </div>
            <div>
              <label htmlFor="ap-type" className="block text-[12px] font-medium text-text-2 mb-1.5">
                Type
              </label>
              <select
                id="ap-type"
                value={form.type}
                onChange={(e) => setForm((p) => ({ ...p, type: e.target.value as SenderType }))}
                disabled={busy}
                className="w-full h-10 px-3 text-sm bg-surface text-text border border-line-strong rounded-md outline-none focus:border-volt-500"
              >
                {SENDER_TYPES.map((tp) => (
                  <option key={tp} value={tp}>
                    {tp}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="ap-usecase" className="block text-[12px] font-medium text-text-2 mb-1.5">
                Use case
              </label>
              <Input
                id="ap-usecase"
                placeholder="marketing, 2fa, …"
                value={form.use_case}
                onChange={(e) => setForm((p) => ({ ...p, use_case: e.target.value }))}
                disabled={busy}
              />
            </div>
          </div>
          <div className="flex items-center justify-end">
            <Button type="submit" disabled={busy}>
              {busy ? 'Adding…' : 'Add as draft'}
            </Button>
          </div>
        </form>
        <p className="text-[12px] text-text-4 mt-3">
          Drafts stay private until you click <strong>Submit</strong>. Submitted entries go to the platform admin for approval.
        </p>
      </Card>

      <Card padding="none">
        <div className="px-5 py-4 border-b border-line">
          <h2 className="display text-[16px] font-semibold text-text">My pool</h2>
        </div>

        {actionError && (
          <div className="px-5 py-2 bg-danger-bg border-b border-danger-bd text-[13px] text-danger">{actionError}</div>
        )}

        {list.isLoading ? (
          <div className="p-5 space-y-2.5">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : list.isError ? (
          <div className="p-8 text-center">
            <p className="text-[13px] text-danger">Failed to load pool.</p>
            <Button variant="secondary" size="sm" className="mt-3" onClick={() => void list.refetch()}>
              Retry
            </Button>
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <p className="text-[14px] text-text-2 font-medium">No senders in your pool yet</p>
            <p className="text-[13px] text-text-3 mt-1">Use the form above to add your first number.</p>
          </div>
        ) : (
          <div className={cn('overflow-x-auto')}>
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-text-4 bg-surface-2 border-b border-line">
                  <th className="px-5 py-2.5 font-medium">Value</th>
                  <th className="px-5 py-2.5 font-medium">Type</th>
                  <th className="px-5 py-2.5 font-medium">Country</th>
                  <th className="px-5 py-2.5 font-medium">Status</th>
                  <th className="px-5 py-2.5 font-medium">Assigned to</th>
                  <th className="px-5 py-2.5 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <PoolRow key={s.id} s={s} busy={false} setError={setActionError} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
